import { connect, type ConnectedProps } from "react-redux";
import { intersection } from "lodash";
import cn from "classnames";

import type Icon from "../../../../../../interfaces/icon";
import { PackStatusLabelWithIcon } from "../../../../../../components/packs/packStatusLabelWithIcon/PackStatusLabelWithIcon";
import { PackPublisherInfo } from "../../../../../../components/packs/packPublisherInfo/PackPublisherInfo";
import { RolePermissions } from "../../../../../../enums";
import RestrictedByTooltip from "../../../../../../components/restrictedByTooltip/RestrictedByTooltip";
import { type RootState } from "../../../../../Application/globaltypes/redux";
import { PackArchivedLabel } from "../../../../../../components/packs/packsList/packArchivedLabel/PackArchivedLabel";
import { permissionsToAccountSelector } from "../../../state/slices/permissionsToAccountSlice";
import SelectableCard from "../../../../../../components/cards/selectableCard/SelectableCard";
import CardContent from "../../../../../../components/cards/cardContent/CardContent";
import CardThumbnail from "../../../../../../components/cards/cardThumbnail/CardThumbnail";
import { type AccountPack, AccountTypes } from "../../../../types";
import "./accountPackCard.scss";
import { useMemo } from "react";
import { AccountPackMenu } from "features/shared/Packs/AccountPackMenu/AccountPackMenu";
import dateTimeUtils from "../../../../../../utils/dateTimeUtils";
import { PricingTypes } from "enums/licensing/pricingTypes";
import type { PackLabel } from "../../../../../Licensing/Packs/PackCard/types";
import { PACK_LABELS } from "../../../../../Licensing/Packs/PackCard/types";

export interface AccountPackCardProps {
  item: AccountPack;
  selected: boolean;
  disabled: boolean;
  url: string;
  moboAccountId?: number;
  onSelect: (id: number) => void;
  icons?: Icon[];
  onEditLicensing?: (id: number) => void;
  onExtendLicenses?: (id: number, title: string, thumbnailImageUrl: string) => void;
  onRemovePackFromAccount?: (id: number) => void;
  isAllowedToRemovePackFromAccount?: () => boolean;
  onArchivePack?: (id: number) => void;
  onRestorePack?: (id: number, title: string, thumbnailImageUrl: string) => void;
  onConvertTrialLicense?: (id: number) => void;
  hasPurchasePower: boolean;
  hasPurchasePackAddon: boolean;
  isAccountHome: boolean;
}

export type AccountPacksCardAllProps = AccountPackCardProps & PropsFromRedux;

export function AccountPackCard(props: AccountPacksCardAllProps) {
  const {
    onSelect,
    url,
    moboAccountId,
    icons,
    onEditLicensing,
    onRemovePackFromAccount,
    isAllowedToRemovePackFromAccount,
    onExtendLicenses,
    onArchivePack,
    onRestorePack,
    onConvertTrialLicense,
    userPermissions,
    hasPurchasePower,
    isAccountHome,
    item,
    accountTypeId,
    hasPurchasePackAddon,
  } = props;
  const {
    id,
    thumbnailImageUrl,
    title,
    description,
    isTrial,
    isArchived,
    isOwn,
    isExpired,
    publisher,
    hasSource,
    licensesCount,
    expirationDate,
    packPricingTypeId,
    pricePerUserPerMonth,
  } = item;

  const hasAccountManageRole = intersection(userPermissions, [RolePermissions.AccountsSettingsManage]).length > 0;
  const isOwnPack = isOwn;
  const isFree = packPricingTypeId === PricingTypes.Free;
  const isOther = packPricingTypeId === PricingTypes.Other;
  const isArchivedPack = Boolean(isArchived);
  const isTrialPack = Boolean(isTrial);
  const isExpiredPack = Boolean(isExpired);

  let formattedExpirationDate = "Expires: Never";
  if (!isFree && !isOwn) {
    const prefix = isExpired ? "Expired" : "Expires";
    formattedExpirationDate = `${prefix} ${dateTimeUtils.formatDate(expirationDate)}`;
  }

  const label = useMemo(() => {
    if (isOther) {
      return null;
    }

    const labelSequence: Array<[boolean, PackLabel]> = [
      [isArchivedPack, PACK_LABELS.archived],
      [isTrialPack && isExpiredPack, PACK_LABELS.trialEnded],
      [isTrialPack, PACK_LABELS.trialStarted],
      [isFree && isExpiredPack, PACK_LABELS.freeExpired],
      [isFree, PACK_LABELS.freeAdded],
      [isExpiredPack, PACK_LABELS.expired],
    ];

    return labelSequence.find(([condition]) => condition)?.[1] ?? PACK_LABELS.purchased;
  }, [isArchivedPack, isExpiredPack, isFree, isTrialPack, isOther]);

  return (
    <SelectableCard
      id={id}
      moboAccountId={moboAccountId}
      className={cn("account-pack-card")}
      onSelect={onSelect}
      disabled={isArchived}
      url={!isArchived ? url : ""}
    >
      <CardThumbnail
        thumbnailUrl={thumbnailImageUrl}
        disabled={isArchived}
        label={label?.name}
        labelColor={label?.color}
        labelTooltipContent={formattedExpirationDate}
      />
      <CardContent
        title={title}
        icons={icons}
        preHeader={<PackPublisherInfo publisherName={publisher} disabled={isArchived} />}
        postHeader={<PackArchivedLabel isArchived={isArchived} />}
        disabled={isArchived}
      >
        <div className="description-row">{description}</div>
        <div className="bottom-row">
          <PackStatusLabelWithIcon isRequested={false} isOwn={isOwn} />
          <RestrictedByTooltip hasPermission={hasAccountManageRole}>
            <div onClick={(e) => e.preventDefault()}>
              <AccountPackMenu
                id={id}
                title={title}
                thumbnailImageUrl={thumbnailImageUrl}
                isExpired={isExpired}
                isTrial={isTrial}
                isArchived={isArchived || false}
                onEditLicensing={onEditLicensing}
                onExtendLicenses={onExtendLicenses}
                onRemovePackFromAccount={onRemovePackFromAccount}
                isAllowedToRemovePackFromAccount={isAllowedToRemovePackFromAccount}
                onArchivePack={!isArchived ? onArchivePack : undefined}
                hasAccountManageRole={hasAccountManageRole}
                onRestorePack={isArchived ? onRestorePack : undefined}
                onConvertTrialLicense={onConvertTrialLicense}
                hasPurchasePower={hasPurchasePower}
                isAccountHome={isAccountHome}
                isOwnPack={isOwnPack}
                hasSource={hasSource}
                packPricingTypeId={packPricingTypeId}
                licenseCount={licensesCount || 0}
                expirationDate={expirationDate}
                hasGlobalAccess={accountTypeId === AccountTypes.BSI || accountTypeId === AccountTypes.ChannelPartner}
                hasPurchasePackAddon={hasPurchasePackAddon}
                pricePerUserPerMonth={pricePerUserPerMonth}
              />
            </div>
          </RestrictedByTooltip>
        </div>
      </CardContent>
    </SelectableCard>
  );
}

const mapStateToProps = (state: RootState) => ({
  userPermissions: permissionsToAccountSelector(state),
  accountTypeId: state.userProfile.accountTypeId,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AccountPackCard);
export default ConnectedComponent;
